/**
 * Inline responses for endpoints that don't need fixture files.
 * Consumed by the snapshot test setup to register MSW handlers and
 * re-exported from endpoints.tsx for the live MSW worker. Extracted
 * to keep endpoints.tsx under the 400-line file-length limit as more
 * inline mocks accumulate.
 *
 * Feature-specific groups live in sibling files (e.g.
 * `companyTodosInlineResponses.tsx`) and are spread in below so a
 * single ENV-wide map is exported.
 */
import { COMPANY_TODOS_INLINE_RESPONSES } from "./companyTodosInlineResponses";
import { CARRIER_TODOS_AND_MEETINGS_INLINE_RESPONSES } from "./carrierTodosAndMeetingsInlineResponses";
import { CARRIER_CRM_INLINE_RESPONSES } from "./carrierCrmInlineResponses";
import { COMPANY_MEETINGS_INLINE_RESPONSES } from "./companyMeetingsInlineResponses";
import { AD_SPEND_INLINE_RESPONSES } from "./adSpendInlineResponses";
import { BACKFILLS_INLINE_RESPONSES } from "./backfillsInlineResponses";
import { TRIPWIRES_INLINE_RESPONSES } from "./tripwiresInlineResponses";
import { SAVED_VIEWS_INLINE_RESPONSES } from "./savedViewsInlineResponses";
import { KLAVIYO_INLINE_RESPONSES } from "./klaviyoInlineResponses";
import { MOBILE_DEVICES_INLINE_RESPONSES } from "./mobileDevicesInlineResponses";
import { CARRIER_PORTAL_INLINE_RESPONSES } from "./carrierPortalInlineResponses";
import { INTEGRATIONS_INLINE_RESPONSES } from "./integrationsInlineResponses";
import { PEOPLE_INTERVIEWS_INLINE_RESPONSES } from "./peopleInterviewsInlineResponses";
import { PEOPLE_TEAM_MEETINGS_INLINE_RESPONSES } from "./peopleTeamMeetingsInlineResponses";
import { COMPANY_API_RATE_LIMITS_INLINE_RESPONSES } from "./companyApiRateLimitsInlineResponses";
import { EDI_INLINE_RESPONSES } from "./ediInlineResponses";
import { CARRIER_INVOICE_INLINE_RESPONSES } from "./carrierInvoiceInlineResponses";
import { CANCEL_SHIPMENT_INLINE_RESPONSES } from "./cancelShipmentInlineResponses";
import { COMPANY_PEOPLE_INLINE_RESPONSES } from "./companyPeopleInlineResponses";
import { COMPANY_SAVED_BROKERS_INLINE_RESPONSES } from "./companySavedBrokersInlineResponses";
import { PUBLIC_API_REQUEST_AUDIT_INLINE_RESPONSES } from "./publicApiRequestAuditInlineResponses";
import { COMPANY_WEBHOOK_DELIVERIES_INLINE_RESPONSES } from "./companyWebhookDeliveriesInlineResponses";
import { CARRIER_PRO_BLOCK_INLINE_RESPONSES } from "./carrierProBlockInlineResponses";
import { INVOICE_CHALLENGE_INLINE_RESPONSES } from "./invoiceChallengeInlineResponses";

export const INLINE_RESPONSES: Record<
  string,
  Record<string, unknown> | unknown[]
> = {
  ...CARRIER_PRO_BLOCK_INLINE_RESPONSES,
  ...INVOICE_CHALLENGE_INLINE_RESPONSES,
  ...COMPANY_TODOS_INLINE_RESPONSES,
  ...CARRIER_TODOS_AND_MEETINGS_INLINE_RESPONSES,
  ...CARRIER_CRM_INLINE_RESPONSES,
  ...COMPANY_MEETINGS_INLINE_RESPONSES,
  ...PEOPLE_INTERVIEWS_INLINE_RESPONSES,
  ...PEOPLE_TEAM_MEETINGS_INLINE_RESPONSES,
  ...AD_SPEND_INLINE_RESPONSES,
  ...BACKFILLS_INLINE_RESPONSES,
  ...TRIPWIRES_INLINE_RESPONSES,
  ...SAVED_VIEWS_INLINE_RESPONSES,
  ...KLAVIYO_INLINE_RESPONSES,
  ...MOBILE_DEVICES_INLINE_RESPONSES,
  ...CARRIER_PORTAL_INLINE_RESPONSES,
  ...INTEGRATIONS_INLINE_RESPONSES,
  ...COMPANY_API_RATE_LIMITS_INLINE_RESPONSES,
  ...EDI_INLINE_RESPONSES,
  ...CANCEL_SHIPMENT_INLINE_RESPONSES,
  ...COMPANY_PEOPLE_INLINE_RESPONSES,
  ...COMPANY_SAVED_BROKERS_INLINE_RESPONSES,
  ...PUBLIC_API_REQUEST_AUDIT_INLINE_RESPONSES,
  ...COMPANY_WEBHOOK_DELIVERIES_INLINE_RESPONSES,
  // Customs tab on /operational-tasks. The shape (CompanyShipmentQuote[])
  // differs from get-active-shipments.json (which returns the full
  // ActiveShipments grouping object), so an alias doesn't work — return an
  // empty list under MSW. Live backend serves real data.
  "/apollo/shipments/get-pending-customs-clearance": [],
  // Integration test endpoints — return a healthy ping for snapshots
  "/apollo/integrations/test-integration": {
    ok: true,
    statusCode: 200,
    responseBody: '{"ok":true,"now":"2026-05-06T12:00:00Z"}',
  },
  "/apollo/integrations/test-netsuite-connection": {
    ok: true,
    statusCode: 200,
    responseBody: '{"ok":true,"now":"2026-05-06T12:00:00Z"}',
  },
  // Customer-sendable PDFs authored in marketing/pdf-sources (the email composer
  // and Front plugin attach these by slug). A small sample for snapshots.
  "/apollo/attachments/pdf-sources": [
    {
      slug: "what-is-freightsimple",
      title: "What is FreightSimple?",
      description: "Company one-pager for prospects",
      downloadFilename: "What-is-FreightSimple.pdf",
    },
    {
      slug: "payment-methods",
      title: "Payment Methods Guide",
      description: "How customers can pay",
      downloadFilename: "FreightSimple-Payment-Methods.pdf",
    },
  ],
  // Thumbnail endpoints return "no image" — real thumbnails are fetched from S3
  "/apollo/carrier-documents/download-thumbnails": { noImage: true },
  "/apollo/company-documents/download-thumbnails": { noImage: true },
  "/apollo/documents/download-thumbnails": { noImage: true },
  "/apollo/documents/download-thumbnail": { noImage: true },
  // Agent tasks — sample data for snapshots
  "/apollo/technical/agent-task-runs": [
    {
      agentTaskRunId: "a1b2c3d4-0001-0001-0001-000000000001",
      agentType: "company-verification",
      status: "completed",
      companyId: "11111111-1111-1111-1111-111111111111",
      requestedBy: "chris@freightsimple.com",
      startedAt: "2026-04-14T18:00:00Z",
      completedAt: "2026-04-14T18:04:12Z",
      ecsTaskArn:
        "arn:aws:ecs:us-east-1:123456:task/freightsimple-production/abc123",
    },
    {
      agentTaskRunId: "a1b2c3d4-0001-0001-0001-000000000002",
      agentType: "sales-intelligence",
      status: "running",
      companyId: "22222222-2222-2222-2222-222222222222",
      requestedBy: "sam@freightsimple.com",
      startedAt: "2026-04-14T18:10:00Z",
      ecsTaskArn:
        "arn:aws:ecs:us-east-1:123456:task/freightsimple-production/def456",
    },
    {
      agentTaskRunId: "a1b2c3d4-0001-0001-0001-000000000003",
      agentType: "company-verification",
      status: "failed",
      companyId: "33333333-3333-3333-3333-333333333333",
      requestedBy: "chris@freightsimple.com",
      startedAt: "2026-04-14T17:30:00Z",
      completedAt: "2026-04-14T17:30:05Z",
      errorMessage: "ECS_CLUSTER_ARN not set",
    },
    {
      agentTaskRunId: "a1b2c3d4-0001-0001-0001-000000000004",
      agentType: "sales-intelligence",
      status: "timed_out",
      companyId: "44444444-4444-4444-4444-444444444444",
      requestedBy: "sam@freightsimple.com",
      startedAt: "2026-04-14T16:00:00Z",
      completedAt: "2026-04-14T16:15:00Z",
      errorMessage: "Task did not report completion within 15 minutes",
    },
    {
      agentTaskRunId: "a1b2c3d4-0001-0001-0001-000000000005",
      agentType: "invoice-audit",
      status: "completed",
      shipmentId: "55555555-5555-5555-5555-555555555555",
      requestedBy: "system:edi",
      startedAt: "2026-04-14T15:00:00Z",
      completedAt: "2026-04-14T15:08:00Z",
    },
  ],
  // Technical tab actions — lightweight responses for snapshot/story rendering
  "/apollo/shipments/generate-scenario-code-for-shipment": {
    scenario: "// Generated scenario code\nval shipment = createShipment()",
  },
  ...CARRIER_INVOICE_INLINE_RESPONSES,
  // Carrier Emails panel on the Tracking tab — empty list hides the panel and
  // keeps the tab snapshots stable; the panel's own story passes data as props.
  "/apollo/shipments/carrier-emails-for-shipment": [],
  // Carrier history dialog — shipments per carrier for audit history
  "/apollo/shipments/shipments-per-carrier-with-audits": [],
  // CSP opportunity email — the rendered preview the send dialog shows.
  "/apollo/customer-specific-pricing/preview-opportunity-email": {
    addresses: ["pricing@example-carrier.com"],
    subject: "CSP Opportunity: Pacific Packaging",
    emailBody:
      '<div style="font-family: Helvetica, sans-serif; font-size: 14px;"><p>Pacific Packaging</p><p>Customer specific pricing opportunity for ABF Freight</p><p>Monthly Freight Spend: $40,000</p><p><a href="https://carriers.freightsimple.com/opportunities/abc">See the full opportunity</a></p></div>',
  },
  // Pricing tab Usage column — empty list keeps snapshots stable; real story data
  // gets injected per-row in the cell stories file.
  "/apollo/customer-specific-pricing/agreement-usage-stats": [],
  // Stickies — default is no stickies for any entity, which keeps the
  // shipment/carrier page snapshots stable (the editable add tile still shows).
  "/apollo/stickies/list": [],
  // Sticky history — sample rows for the history dialog (newest first).
  "/apollo/stickies/history": [
    {
      historyId: "11111111-0000-0000-0000-000000000002",
      stickyId: "11111111-1111-1111-1111-111111111111",
      entityType: "company",
      entityId: "44b5bdb5-5352-4510-aee5-65c03611082d",
      action: "updated",
      title: "Check with sales before additional charges",
      description: "First 20 shipments need sign-off from Natasha.",
      category: "finance",
      editedBy: "maria.cubides@freightsimple.com",
      editedAt: "2026-04-20T14:32:00Z",
    },
    {
      historyId: "11111111-0000-0000-0000-000000000001",
      stickyId: "11111111-1111-1111-1111-111111111111",
      entityType: "company",
      entityId: "44b5bdb5-5352-4510-aee5-65c03611082d",
      action: "created",
      title: "Check with sales before charges",
      description: "Awaiting sign-off.",
      category: "finance",
      editedBy: "maria.cubides@freightsimple.com",
      editedAt: "2026-04-18T09:15:00Z",
    },
  ],
  // Claude conversation — sample exchange for snapshot/story rendering
  "/apollo/claude/get-claude-conversation-items": [
    {
      role: "user",
      html: "<p>What carrier did we book for this shipment and why did it take so long to quote?</p>",
      apolloEmail: "bruno@freightsimple.com",
      createdAt: "2026-04-23T14:30:00Z",
    },
    {
      role: "assistant",
      html: "<p>You booked Dayton Freight on the afternoon of April 20. The quote took 42 seconds because three carriers required manual review from the ratesheet pipeline: XPO, Estes, and Saia — all cross-border to Blaine, WA.</p>",
      apolloEmail: "",
      createdAt: "2026-04-23T14:30:08Z",
    },
  ],
  // Claude send — returns void; MSW needs an empty handler so the POST doesn't 404
  "/apollo/claude/send-claude-message": {},
  // Email-for-quote dialog — eligible carriers list and email preview.
  // Real backend serves carrier matrix; inline response covers a representative
  // multi-carrier set so the carrier filter MultiSelect is populated for snapshots.
  "/apollo/quotes/fetch-carriers-that-provide-email-quotes": [
    {
      carrierIdentifier: "abf",
      carrierDisplayName: "ABF Freight",
      serviceIdentifier: "standard",
      serviceDisplayName: "Standard LTL",
      quotingActivationStrategyType: "Active",
    },
    {
      carrierIdentifier: "estes",
      carrierDisplayName: "Estes Express",
      serviceIdentifier: "standard",
      serviceDisplayName: "Standard LTL",
      quotingActivationStrategyType: "Active",
    },
    {
      carrierIdentifier: "saia",
      carrierDisplayName: "Saia LTL Freight",
      serviceIdentifier: "standard",
      serviceDisplayName: "Standard LTL",
      quotingActivationStrategyType: "Active",
    },
  ],
  "/apollo/quotes/preview-request-quote-email": {
    addresses: ["dispatch@example-carrier.com"],
    subject: "Quote request for shipment sh_abc123",
    emailBody:
      '<div style="font-family: Calibri, sans-serif; font-size: 14px;"><p>Hi team,</p><p>Please send a quote for the attached shipment.</p></div>',
  },
  // Company verified email — preview rendered before sending the "your company
  // is verified" confirmation to the company's dashboard users. Recipients are
  // seeded from getCompanyUsers; preview is editable through EmailDialog.
  // Customs delay notification email — default content fetched on dialog open
  // to seed the editable subject + body fields.
  "/apollo/support-email/customs-delay-notification-default-content": {
    subject: "Customs Delay Notification",
    body: "Saia LTL Freight have informed us of a customs delay on this shipment.\n\nIf you haven't already, you would need to contact your customs broker to find out further details of the delay.\n\nPlease note customs delays risk storage and bond charges being incurred, so it's important to resolve them as quickly as possible.\n\nYou can view full details of the shipment here:\nhttps://app.freightsimple.com/tracking/sh_abc123",
    recipients: ["operations@example-shipper.com"],
    brokerEmail: "clearance@example-broker.com",
  },
  // Request-photos-of-missing-freight email — default content fetched on dialog
  // open to seed the editable subject + body fields.
  "/apollo/support-email/request-photos-of-missing-freight-default-content": {
    subject: "Missing Freight : Photos Requested",
    body: "I'm very sorry - Saia LTL Freight have informed us that they currently do not know the location of this shipment.\n\nTypically missing shipments show up with in a few days, but we always want to get this resolved as quickly as possible.\n\nDo you have photos or any other distinguishing information about this shipment that we can pass to the carrier?\n\nYou can view full details of the shipment here:\nhttps://app.freightsimple.com/tracking/sh_abc123",
  },
  "/apollo/support-email/preview-company-verified-email": {
    addresses: [
      "operations@example-shipper.com",
      "finance@example-shipper.com",
    ],
    subject: "Welcome to FreightSimple — your account is verified",
    emailBody:
      '<div style="font-family: Calibri, sans-serif; font-size: 14px;"><p>Hi team,</p><p>Your FreightSimple account has been verified — you can now book shipments and view invoices.</p><p>Reach out any time if you need a hand getting started.</p></div>',
  },
  // Generic email preview — live preview pane of the shipment "Send Email"
  // compose dialog (EmailDialog fires it on open).
  "/apollo/support-email/preview-generic-email": {
    addresses: ["customer.service@dayross.com"],
    subject: "Toronto to Vancouver (BOL: BOL-12345, PRO: PRO-67890)",
    emailBody:
      '<div style="font-family: Calibri, sans-serif; font-size: 14px;"><p>Hi,</p><p>We are reaching out about this shipment.</p><p>Thanks,<br/>FreightSimple Support</p></div>',
  },
  // Signal prefixes — populates the "Signal name prefix" dropdown in the
  // Add Alarm Rule dialog. Real backend returns every prefix observed in the
  // signals table; inline list mirrors the carriers in the rules fixture.
  "/apollo/signals/get-signal-prefixes": [
    "carrier.polaris-transportation",
    "carrier.kindersley-transport",
    "carrier.day-and-ross",
    "carrier.fedex-freight",
    "carrier.tforce-freight",
    "carrier.estes",
  ],
  // Single-quote lookup used by the notes-timeline
  // "View Previous/New Quote" buttons on selected-quote-change apollo_action
  // notes. The buttons hand the timeline a quoteId and we fetch the
  // historical quote on demand — most stories only need a Quote shape that
  // renders cleanly inside ViewShipmentQuoteDetailsDialog.
  "/apollo/quotes/get-quote-by-id": {
    quoteId: "00000000-0000-0000-0000-000000000000",
    carrierIdentifier: "estes",
    serviceIdentifier: "standard",
    priceCad: 1000,
    quotedPriceByCarrierCad: 850,
    transitBusinessDays: 3,
    latestTransitBusinessDays: 5,
    includesTax: false,
    exclusiveUseNeeded: false,
    hiddenFromCustomer: false,
    createdAt: "2026-04-01T12:00:00Z",
    lineItems: [],
    pickupAccessorials: [],
    deliveryAccessorials: [],
  },
  // Company Lists — owned-by/followed-by view from the demo user
  "/apollo/company-lists/get-all-lists": [
    {
      companyListId: "00000000-1234-0000-0000-000000000001",
      name: "Q2 Toronto Visits",
      ownerEmail: "demo@freightsimple.com",
      itemCount: 12,
      doneCount: 3,
      isOwner: true,
      isFollowing: false,
      followers: ["chris@freightsimple.com"],
    },
    {
      companyListId: "00000000-1234-0000-0000-000000000002",
      name: "Mississauga Cold Lanes",
      ownerEmail: "chris@freightsimple.com",
      itemCount: 8,
      doneCount: 0,
      isOwner: false,
      isFollowing: true,
      followers: [
        "demo@freightsimple.com",
        "edgardo.valencia@freightsimple.com",
      ],
    },
    {
      companyListId: "00000000-1234-0000-0000-000000000003",
      name: "March Hot Leads",
      ownerEmail: "edgardo.valencia@freightsimple.com",
      itemCount: 5,
      doneCount: 5,
      isOwner: false,
      isFollowing: false,
      followers: [],
    },
  ],
  // Company List detail — pins centred around Toronto for the map view
  "/apollo/company-lists/get-list-detail": {
    companyListId: "00000000-1234-0000-0000-000000000001",
    name: "Q2 Toronto Visits",
    ownerEmail: "demo@freightsimple.com",
    isOwner: true,
    isFollowing: false,
    followers: ["chris@freightsimple.com"],
    items: [
      // prettier-ignore
      { companyListItemId: "00000000-aaaa-0000-0000-000000000001", companyId: "00000000-1111-0000-0000-000000000001", companyName: "Acme Logistics", billingCity: "Toronto", billingState: "ON", billingLatitude: 43.65107, billingLongitude: -79.347015, position: 0, done: false, note: "Call ahead before visiting" },
      // prettier-ignore
      { companyListItemId: "00000000-aaaa-0000-0000-000000000002", companyId: "00000000-1111-0000-0000-000000000002", companyName: "Brampton Distribution", billingCity: "Brampton", billingState: "ON", billingLatitude: 43.7315, billingLongitude: -79.7624, position: 1, done: true, note: undefined },
      // prettier-ignore
      { companyListItemId: "00000000-aaaa-0000-0000-000000000003", companyId: "00000000-1111-0000-0000-000000000003", companyName: "Mississauga Freight", billingCity: "Mississauga", billingState: "ON", billingLatitude: 43.589, billingLongitude: -79.6441, position: 2, done: false, note: undefined },
    ],
  },
  // Optimization stubs — return the input order unchanged so the UI flow renders.
  "/apollo/company-lists/optimize-route": {
    orderedCompanyListItemIds: [
      "00000000-aaaa-0000-0000-000000000001",
      "00000000-aaaa-0000-0000-000000000003",
      "00000000-aaaa-0000-0000-000000000002",
    ],
    startLatitude: 43.6772,
    startLongitude: -79.6306,
  },
  // Void POSTs: the live backend returns 200 with no body. The generated
  // client wraps these in VoidApiResponse, which never reads the response
  // body — any JSON-serializable value works here; we use {} by convention
  // to make it obvious there's no meaningful payload.
  "/apollo/company-lists/clear-route": {},
  "/apollo/company-lists/bulk-set-item-done": {},
  // Batch assign/unassign the sales agent — void POST, needs a stub so the bulk
  // action's success path works under mock data instead of bypassing to the backend.
  "/apollo/companies/assign-companies": {},
};
